// Angular
import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Material
import { MatDialog } from '@angular/material';
// RxJS
import { Observable, BehaviorSubject, Subscription, of, concat } from 'rxjs';
import { map, startWith, delay, first, distinctUntilChanged } from 'rxjs/operators';
// NGRX
import { Store, select } from '@ngrx/store';
import { Dictionary, Update } from '@ngrx/entity';
import { AppState } from '../../../../../core/reducers';
// Layout
import { SubheaderService, LayoutConfigService } from '../../../../../core/_base/layout';
// CRUD
import { LayoutUtilsService, TypesUtilsService, MessageType } from '../../../../../core/_base/crud';
// Services and Models
import { 
	GrainType,
	GrainTypeGetAll,
	GrainTypesDataSource,
	selectLastCreatedSiloId, 
	selectSiloById, 
	Silo, 
	SiloAdd, 
	SiloEdit
} from '../../../../../core/plant-configuration';
import { User } from '../../../../../core/auth';
import { selectAuthState } from '../../../../../core/auth/_selectors/auth.selectors';

@Component({
  selector: 'kt-silos-edit',
  templateUrl: './silos-edit.component.html',
  styleUrls: ['./silos-edit.component.scss']
})
export class SilosEditComponent implements OnInit, OnDestroy {
	// Public properties
	silo: Silo;
	siloId$: Observable<number>;
	oldSilo: Silo;
	selectedTab = 0;
	loadingSubject = new BehaviorSubject<boolean>(true);
	loading$: Observable<boolean>;
	siloForm: FormGroup;
	hasFormErrors = false;
  	filteredGrainTypes: Observable<{id:number, name:string}[]>;
  	grainTypesDataSource: GrainTypesDataSource;
	grainTypesResult: GrainType[];
	private subscriptions: Subscription[] = [];
	private user:User = new User();

	// Private password
	private componentSubscriptions: Subscription;
	// sticky portlet header margin
	private headerMargin: number;
	private selectedGrainType: GrainType = null;

	/**
	 * Component constructor
	 *
	 * @param store: Store<AppState>
	 * @param activatedRoute: ActivatedRoute
	 * @param router: Router
	 * @param typesUtilsService: TypesUtilsService
	 * @param formBuilder: FormBuilder
	 * @param dialog: MatDialog
	 * @param subheaderService: SubheaderService
	 * @param layoutUtilsService: SubheaderService
	 * @param layoutConfigService: LayoutConfigService
	 * @param cdr: ChangeDetectorRef
	 */
	constructor(
		private store: Store<AppState>,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private typesUtilsService: TypesUtilsService,
		private formBuilder: FormBuilder,
		public dialog: MatDialog,
		private subheaderService: SubheaderService,
		private layoutUtilsService: LayoutUtilsService,
		private layoutConfigService: LayoutConfigService,
		private cdr: ChangeDetectorRef) {
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit() {

		this.loading$ = this.loadingSubject.asObservable();
		this.loadingSubject.next(true);

		this.user.clear();

		const authSubscription = this.store.pipe(
			select(selectAuthState),
		).subscribe((auth) => {
			// console.log("auth state: "+JSON.stringify(auth));
			if(auth.isUserLoaded)
				this.user = auth.user;
			else
				console.log("silos edit component: usuario no logueado");
		});
		    
    	// Init DataSource
		this.grainTypesDataSource = new GrainTypesDataSource(this.store);
		const entitiesSubscription = this.grainTypesDataSource.entitySubject.pipe(
			distinctUntilChanged()
		).subscribe(res => {
			this.grainTypesResult = res;
			this.activatedRoute.params.subscribe(params => {
				const id = params.id;
				if (id && id > 0) {
	
					const sub = this.store.pipe(
						select(selectSiloById(id))
					).subscribe(result => {
						// if (!result) {
						// 	this.loadProducFromService(id);
						// 	return;
						// }
						console.log("Silo retrieved from store");
						this.loadSilo(result);
					});
					this.subscriptions.push(sub);
				} else {
					console.log("default silo");
					const newSilo = new Silo();
					newSilo.clear();
					this.loadSilo(newSilo);
				}
			});
		});
		this.subscriptions.push(entitiesSubscription);
		this.subscriptions.push(authSubscription);
		this.store.dispatch(new GrainTypeGetAll({}));

		// sticky portlet header
		window.onload = () => {
			const style = getComputedStyle(document.getElementById('kt_header'));
			this.headerMargin = parseInt(style.height, 0);
		};
	}

	loadSilo(silo, fromService: boolean = false) {
		if (!silo) {
			this.goBack('');
		}
		this.silo = silo;
		this.selectedGrainType = new GrainType();
		this.selectedGrainType.clear();
		this.selectedGrainType.id = silo.grain_type_id;
		this.selectedGrainType.name = silo.grain_type;
		this.siloId$ = of(silo.id);
		this.oldSilo = Object.assign({}, silo);
		this.initSilo();
		if (fromService) {
			this.cdr.detectChanges();
		}
  }

	/**
	 * On destroy
	 */
	ngOnDestroy() {
		if (this.componentSubscriptions) {
			this.componentSubscriptions.unsubscribe();
		}
		this.subscriptions.forEach((sub)=>sub.unsubscribe());
	}

	/**
	 * Init silo
	 */
	initSilo() {
		this.createForm();
		this.loadingSubject.next(false);
		// if (!this.silo.id) {
		// 	this.subheaderService.setBreadcrumbs([
		// 		{ title: 'Silo list', page: `/plant-configuration/silos` },
		// 		{ title: 'Create silo', page: `/plant-configuration/silos/add` }
		// 	]);
		// 	return;
		// }
		// this.subheaderService.setTitle('Edit silo');
		// this.subheaderService.setBreadcrumbs([
		// 	{ title: 'Silo list', page: `/plant-configuration/silos` },
		// 	{ title: 'Edit silo', page: `/plant-configuration/silos/edit`, queryParams: { id: this.silo.id } }
		// ]);
	}

	/**
	 * Create form
	 */
	createForm() {
		this.siloForm = this.formBuilder.group({
			name: [this.silo.name, Validators.required],
			capacity: [this.silo.capacity, Validators.required],
			pReorder: [this.silo.p_reorder, [Validators.required, Validators.min(0), Validators.max(100)]],
			grainType: [this.silo.grain_type, Validators.required],
		});

		this.filteredGrainTypes = concat(
			of(this.filterGrainType("")),
			this.siloForm.controls.grainType.valueChanges.pipe(
				startWith(''),
				map(val => this.filterGrainType(val.toString()))
			)
		);		
	}

	/**
	 * Filter manufacture
	 *
	 * @param val: string
	 */
	filterGrainType(val: string): {id: number, name:string}[] {
		if (this.grainTypesResult){
			return this.grainTypesResult.filter(grainType =>
				grainType.name.toString().toLowerCase().includes(val.toLowerCase()));
		}
    	return [];
	}

	/**
	 * Go back to the list
	 *
	 * @param id: any
	 */
	goBack(id) {
		this.loadingSubject.next(false);
		const url = `/plant-configuration/silos?id=${id}`;
		this.router.navigateByUrl(url, { relativeTo: this.activatedRoute });
	}

	goBackWithoutId() {
		this.router.navigateByUrl('/plant-configuration/silos', { relativeTo: this.activatedRoute });
	}

	/**
	 * Refresh silo
	 *
	 * @param isNew: boolean
	 * @param id: number
	 */
	refreshSilo(isNew: boolean = false, id = 0) {
		this.loadingSubject.next(false);
		let url = this.router.url;
		if (!isNew) {
			this.router.navigate([url], { relativeTo: this.activatedRoute });
			return;
		}

		url = `/plant-configuration/silos/edit/${id}`;
		this.router.navigateByUrl(url, { relativeTo: this.activatedRoute });
	}

	/**
	 * Reset
	 */
	reset() {
		this.silo = Object.assign({}, this.oldSilo);
		this.createForm();
		this.hasFormErrors = false;
		this.siloForm.markAsPristine();
		this.siloForm.markAsUntouched();
		this.siloForm.updateValueAndValidity();
	}

	/**
	 * Save data
	 *
	 * @param withBack: boolean
	 */
	onSumbit(withBack: boolean = false) {
		this.hasFormErrors = false;
		const controls = this.siloForm.controls;
		/** check form */
		if (this.siloForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);

			this.hasFormErrors = true;
			this.selectedTab = 0;
			return;
		}

		// tslint:disable-next-line:prefer-const
		let editedSilo = this.prepareSilo();

		if (editedSilo.id > 0) {
			this.updateSilo(editedSilo, withBack);
			return;
		}

		this.addSilo(editedSilo, withBack);
	}

	/**
	 * Returns object for saving
	 */
	prepareSilo(): Silo {
		const controls = this.siloForm.controls;
		const silo = new Silo();
		silo.id = this.silo.id;
		silo.name = controls.name.value;
		silo.capacity = controls.capacity.value;
		silo.p_reorder = controls.pReorder.value;
		silo.grain_type_id = this.selectedGrainType.id;
		silo._createdDate = this.silo._createdDate;
		silo._updatedDate = this.silo._updatedDate;
		silo._updatedDate = this.typesUtilsService.getDateStringFromDate();
		silo._createdDate = this.silo.id > 0 ? silo._createdDate : silo._updatedDate;
		silo.write_uid = this.user.id;
		silo.company_id = this.user.company_id;
		console.log(JSON.stringify(silo))
		return silo;
	}

	/**
	 * Add Silo
	 *
	 * @param silo: Silo
	 * @param withBack: boolean
	 */
	addSilo(silo: Silo, withBack: boolean = false) {
		this.loadingSubject.next(true);
		
		this.store.dispatch(new SiloAdd({silo: silo}));
		this.componentSubscriptions = this.store.pipe(
			delay(1000),
			select(selectLastCreatedSiloId)
		).subscribe(newId => {
			if (!newId) {
				return;
			}

			this.loadingSubject.next(false);
			if (withBack) {
				this.goBack(newId);
			} else {
				const message = `New silo successfully has been added.`;
				this.layoutUtilsService.showActionNotification(message, MessageType.Create, 10000, true, true);
				this.refreshSilo(true, newId);
			}
		});
	}

	/**
	 * Update silo
	 *
	 * @param silo: Silo
	 * @param withBack: boolean
	 */
	updateSilo(silo: Silo, withBack: boolean = false) {
		this.loadingSubject.next(true);

		const updateSilo: Update<Silo> = {
			id: silo.id,
			changes: silo
		};

		this.store.dispatch(new SiloEdit({
			partialSilo: updateSilo,
			silo: silo
		}));

		of(undefined).pipe(delay(3000)).subscribe(() => { // Remove this line
			if (withBack) {
				this.goBack(silo.id);
			} else {
				const message = `Silo successfully has been saved.`;
				this.layoutUtilsService.showActionNotification(message, MessageType.Update, 10000, true, true);
				this.refreshSilo(false);
			}
		}); // Remove this line
	}

	/**
	 * Returns component title
	 */
	getComponentTitle() {
		let result = 'Create silo';
		if (!this.silo || !this.silo.id) {
			return result;
		}

		result = `Edit silo - ${this.silo.name} ${this.silo.grain_type}, ${this.silo.capacity}`;
		return result;
	}

	/**
	 * Close alert
	 *
	 * @param $event
	 */
	onAlertClose($event) {
		this.hasFormErrors = false;
	}

	selectGrainType(grainType:GrainType){
		this.selectedGrainType = grainType;
	}
}
